// **Vorlagen eines Mandanten** — die Prompt-Bibliothek, aus der Benutzer in der
// Instanz eine Unterhaltung in einem Arbeitsbereich oder einer Aufgabe starten.
// In einer verwalteten Instanz gibt es den Abschnitt „Vorlagen" nur hier
// (docs/11 6); gespeichert wird in der Instanz (`/api/platform/templates…`),
// mit ihren Regeln. Bearbeitet wird in der Seite, nicht in einem Dialog:
// `?template=<id>|new`, `?category=<id>|new`.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { get } from "../../api/client";
import { keys } from "../../api/queries";
import type { InstanceTemplatesResponse, PromptTemplate } from "../../api/types";
import { errorText } from "../../lib/labels";
import { Link, useNavigate, useSearchParams } from "../../router/router";
import { Loading, Note } from "../../ui/badge";
import { Card } from "../../ui/card";
import { CategoryEditor, TemplateEditor } from "./template-editors";
import type { TabProps } from "./tabs";
import { txt } from "./templates-map";

export function TemplatesTab({ tenant: t }: TabProps) {
  const search = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const base = `/tenants/${t.id}/instance/templates`;
  const here = `/tenants/${t.id}/templates`;
  const q = useQuery({ queryKey: keys.instanceTemplates(t.id), queryFn: () => get<InstanceTemplatesResponse>(base) });
  const editTemplate = search.get("template");
  const editCategory = search.get("category");
  const data = q.data;
  const category = data && editCategory && editCategory !== "new" ? data.categories.find((x) => String(x.id) === editCategory) ?? null : null;
  const template = data && editTemplate && editTemplate !== "new" ? data.templates.find((x) => String(x.id) === editTemplate) ?? null : null;
  // Eine Kennung, die es (nicht mehr) gibt, führt zurück zur Liste.
  const missing = Boolean(data && ((editCategory && editCategory !== "new" && !category) || (!editCategory && editTemplate && editTemplate !== "new" && !template)));
  useEffect(() => { if (missing) navigate(here, { replace: true }); }, [missing, navigate, here]);

  if (q.isPending) return <Card title="Vorlagen"><Loading /></Card>;
  if (q.isError) return <Card title="Vorlagen"><Note tone="error">Die Instanz antwortet nicht: {errorText(q.error)}</Note></Card>;
  if (missing || !data) return null;

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: keys.instanceTemplates(t.id) });
    navigate(here, { replace: true });
  };
  if (editCategory) return <CategoryEditor key={editCategory} category={category} categories={data.categories} base={base} here={here} done={done} />;
  if (editTemplate) return <TemplateEditor key={editTemplate} template={template} categories={data.categories} base={base} here={here} done={done} />;

  const byCat = new Map<number, PromptTemplate[]>(data.categories.map((c) => [c.id, []]));
  for (const tpl of data.templates) byCat.get(tpl.category_id)?.push(tpl);
  return (
    <Card
      title="Vorlagen"
      hint="Vorlagen starten eine Unterhaltung in einem Arbeitsbereich oder einer Aufgabe mit einem fertigen Prompt."
      actions={<>
        <Link className="pf-btn pf-btn--sm" to={`${here}?category=new`}>Neue Kategorie</Link>
        <Link className="pf-btn pf-btn--sm pf-btn--primary" to={`${here}?template=new`}>Neue Vorlage</Link>
      </>}
      flush
    >
      {data.categories.length ? data.categories.map((c) => {
        const list = byCat.get(c.id) ?? [];
        return (
          <section key={c.id} className="pf-tplcat">
            <header className="pf-tplcat__head">
              <span className="pf-subhead">{txt(c.name)}</span>
              <Link className="pf-btn pf-btn--sm pf-btn--ghost" to={`${here}?category=${c.id}`}>Bearbeiten</Link>
            </header>
            {list.length ? (
              <ul className="pf-feed">
                {list.map((tpl) => (
                  <li key={tpl.id}>
                    <Link className="pf-feed__item" to={`${here}?template=${tpl.id}`}>
                      <span className="pf-feed__main"><strong>{txt(tpl.title)}</strong><span className="pf-sub">{txt(tpl.description) || tpl.prompt.slice(0, 120)}</span></span>
                      <span className="pf-feed__meta">{tpl.featured ? <span className="pf-tag">Empfohlen</span> : null}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : <div className="pf-empty pf-empty--sm">Keine Vorlage in dieser Kategorie.</div>}
          </section>
        );
      }) : <div className="pf-empty">Noch keine Vorlagen. Erst eine Kategorie anlegen, dann Vorlagen darin.</div>}
    </Card>
  );
}
